import { Observe } from "./_/observe";
import gsap, { reduced, resetInitial } from "../gsap";
import { computeParams } from "./_/index";

/**
 * Logo Module - "Rearranging Furniture" animation
 *
 * Clean, deliberate single-axis movements like arranging furniture.
 * Each letter moves on one axis at a time - no diagonal wobbling.
 */
export class Logo extends Observe {
  #timeline;
  #parts = {};

  a = {
    delay: 0.1,
  };

  constructor(element) {
    super(element, {
      threshold: 0.2,
      once: true,
    });

    this.create();
    resetInitial(element);
  }

  create() {
    if (reduced) return;

    computeParams(this.element, this.a);

    this.#parts = {
      square: this.element.querySelector(".square"),
      M: this.element.querySelector(".M"),
      A: this.element.querySelector(".A"),
      m: this.element.querySelector(".m"),
      o: this.element.querySelector(".o"),
    };

    const { square, M, A, m, o } = this.#parts;

    // Square: masked from top, will reveal downward
    if (square) {
      gsap.set(square, {
        clipPath: "inset(0% 0% 100% 0%)", // Hidden: clipped from bottom by 100%
      });
    }

    // M: starts below (will slide up)
    if (M) {
      gsap.set(M, {
        y: 120,
        opacity: 0,
        transformOrigin: "center center",
      });
    }

    // A: starts to the right (will slide left)
    if (A) {
      gsap.set(A, {
        x: 150,
        opacity: 0,
        transformOrigin: "center center",
      });
    }

    // m: starts at bottom-right, rotated upright
    // Will slide in, bounce, rotate, then slide left
    if (m) {
      gsap.set(m, {
        x: 380,
        // y: 0,
        rotation: -90, // Upright/readable position
        opacity: 0,
        transformOrigin: "center center",
      });
    }

    // o: will be drawn with stroke animation
    if (o) {
      // Get the path length for stroke animation
      const length = o.getTotalLength?.() || 600;
      gsap.set(o, {
        fill: "transparent",
        stroke: "#FF8967",
        strokeWidth: 2,
        strokeDasharray: length,
        strokeDashoffset: length, // Fully hidden
        transformOrigin: "center center",
      });
    }
  }

  isIn = () => {
    if (reduced) return;
    if (this.#timeline) return;

    const { square, M, A, m, o } = this.#parts;
    const { delay } = this.a;

    this.#timeline = gsap.timeline({ delay });

    // ═══════════════════════════════════════════════════════════════
    // SQUARE - Grows from top with clip-path mask
    // ═══════════════════════════════════════════════════════════════
    if (square) {
      this.#timeline.to(square, {
        clipPath: "inset(0% 0% 0% 0%)", // Fully revealed
        duration: 0.6,
        ease: "power3.inOut",
      });
    }

    // ═══════════════════════════════════════════════════════════════
    // M - Comes in from bottom, slides up with bounce
    // ═══════════════════════════════════════════════════════════════
    if (M) {
      this.#timeline.to(
        M,
        {
          opacity: 1,
          duration: 0.2,
          ease: "power2.out",
        },
        "-=0.2"
      );

      this.#timeline.to(
        M,
        {
          y: 0,
          duration: 0.5,
          ease: "back.out(1.4)",
        },
        "-=0.15"
      );
    }

    // ═══════════════════════════════════════════════════════════════
    // A - Comes in from right, slides left with bounce
    // ═══════════════════════════════════════════════════════════════
    if (A) {
      this.#timeline.to(
        A,
        {
          opacity: 1,
          duration: 0.2,
          ease: "power2.out",
        },
        "-=0.35"
      );

      this.#timeline.to(
        A,
        {
          x: 0,
          duration: 0.5,
          ease: "back.out(1.4)",
        },
        "-=0.15"
      );
    }

    // ═══════════════════════════════════════════════════════════════
    // o - Circle drawn clockwise
    // ═══════════════════════════════════════════════════════════════
    if (o) {
      const length = o.getTotalLength?.() || 600;

      this.#timeline.to(
        o,
        {
          strokeDashoffset: 0,
          strokeWidth: 6,
          duration: 0.6,
          ease: "power2.inOut",
        },
        "-=0.3"
      );

      // Fill in after stroke completes
    //   this.#timeline.to(
    //     o,
    //     {
    //       fill: "#FF8967",
    //       stroke: "transparent",
    //       duration: 0.25,
    //       ease: "power2.out",
    //     },
    //     "-=0.1"
    //   );
    }

    // ═══════════════════════════════════════════════════════════════
    // m - Comes from bottom-right, stops with bounce, rotates, slides left
    // Single axis at a time!
    // ═══════════════════════════════════════════════════════════════
    if (m) {
      // Fade in
      this.#timeline.to(
        m,
        {
          opacity: 1,
          duration: 0.15,
          ease: "power2.out",
        },
        "-=0.5"
      );

      // Phase 1: Slide in to bottom-right staging position with tiny bounce
      this.#timeline.to(
        m,
        {
          x: 250,
          y: 0,
          duration: 0.4,
          ease: "back.out(1.2)",
        },
        "-=0.1"
      );

      // Phase 2: Rotate (rotation only)
      this.#timeline.to(m, {
        rotation: 0,
        duration: 0.35,
        ease: "power3.out",
      });

      // Phase 3: Slide left to final position with tiny bounce
      this.#timeline.to(m, {
        x: 0,
        duration: 0.45,
        ease: "back.out(1.2)",
      });
    }
  };

  isOut = () => {};

  resize() {}

  transitionOut() {
    if (this.#timeline) {
      this.#timeline.kill();
      this.#timeline = null;
    }
    this.destroy();
  }
}